"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AvailabilityForecastSchema = exports.HOUR_KEYS = exports.HourlyScoresSchema = exports.HourlyScoreEntrySchema = exports.SITE_FEATURE_KEYS = exports.SiteFeaturesSchema = exports.SiteCategorySchema = exports.SECURITY_AMENITIES = exports.ESSENTIAL_AMENITIES = exports.ABSOLUTE_MAX_PARKING_PRICE_EUR = exports.DEFAULT_MAX_PARKING_PRICE_EUR = exports.RestAreaFeaturesSchema = exports.RestAreaContactSchema = exports.RestAreaAddressSchema = exports.RestAreaDuplicateSummarySchema = exports.PricesSchema = exports.PeriodUnitSchema = exports.DataOrigin = exports.SiteOpeningStatusSchema = exports.SiteOpeningStatus = exports.SiteAvailabilityStatusSchema = exports.SiteAvailabilityStatus = void 0;
exports.classifyForecastScore = classifyForecastScore;
exports.parseHourlyScores = parseHourlyScores;
const zod_1 = require("zod");
var SiteAvailabilityStatus;
(function (SiteAvailabilityStatus) {
    SiteAvailabilityStatus["AlmostFull"] = "almostFull";
    SiteAvailabilityStatus["Full"] = "full";
    SiteAvailabilityStatus["SpacesAvailable"] = "spacesAvailable";
    SiteAvailabilityStatus["Unknown"] = "unknown";
})(SiteAvailabilityStatus || (exports.SiteAvailabilityStatus = SiteAvailabilityStatus = {}));
exports.SiteAvailabilityStatusSchema = zod_1.z.nativeEnum(SiteAvailabilityStatus);
var SiteOpeningStatus;
(function (SiteOpeningStatus) {
    SiteOpeningStatus["Open"] = "open";
    SiteOpeningStatus["StatusUnknown"] = "statusUnknown";
    SiteOpeningStatus["Unknown"] = "unknown";
})(SiteOpeningStatus || (exports.SiteOpeningStatus = SiteOpeningStatus = {}));
exports.SiteOpeningStatusSchema = zod_1.z.nativeEnum(SiteOpeningStatus);
var DataOrigin;
(function (DataOrigin) {
    DataOrigin["TOLLCOLLECT"] = "TollCollect";
    DataOrigin["PARKYOURTRUCK"] = "ParkYourTruck";
    DataOrigin["BOSCH"] = "BoschSecureParking";
    DataOrigin["EURORASTPARK"] = "Eurorastpark";
    DataOrigin["ITP"] = "ITP";
    DataOrigin["AUTOHOF"] = "Autohof";
    DataOrigin["KRAVAG"] = "Kravag";
    DataOrigin["ROATEL"] = "Roatel";
    DataOrigin["NETHERLANDS"] = "Netherlands";
    DataOrigin["LUXEMBOURG"] = "Luxembourg";
    DataOrigin["BELGIUM"] = "Belgium";
    DataOrigin["AUSTRIA"] = "ASFINAG";
})(DataOrigin || (exports.DataOrigin = DataOrigin = {}));
exports.PeriodUnitSchema = zod_1.z.enum([
    'hour',
    'day',
    'week',
    'month',
]);
// An explicit z.ZodObject<Shape> annotation truncates the generated .d.ts to a partial
// generic; consumers with strictNullChecks:false (e.g. routing-functions) then recompute
// Output/Input and every field turns optional. Leave this to inference so the compiled
// .d.ts bakes in the full, consumer-proof type.
exports.PricesSchema = zod_1.z.object({
    periodValue: zod_1.z.number(),
    period: exports.PeriodUnitSchema,
    description: zod_1.z.string(),
    netPrice: zod_1.z.number(),
    paymentFee: zod_1.z.number(),
    reservationFee: zod_1.z.number(),
    vatAmount: zod_1.z.number(),
    vatRate: zod_1.z.string(),
    netTotal: zod_1.z.number(),
    grossPrice: zod_1.z.number(),
    isOffer: zod_1.z.boolean(),
    startDate: zod_1.z.string().nullable(),
    endDate: zod_1.z.string().nullable(),
    voucherAmount: zod_1.z.number().optional(),
});
/** Lightweight summary of another provider's entry for the same physical location. */
exports.RestAreaDuplicateSummarySchema = zod_1.z.object({
    id: zod_1.z.string(),
    origin: zod_1.z.nativeEnum(DataOrigin),
    externalId: zod_1.z.string(),
    title: zod_1.z.string(),
    isBookable: zod_1.z.boolean(),
});
/** Postal address of a rest area, as stored on `RestAreaEntity.address`/`RestAreaOverrideEntity.address`. */
exports.RestAreaAddressSchema = zod_1.z.object({
    street: zod_1.z.string().nullable(),
    houseNumber: zod_1.z.string().nullable(),
    postcode: zod_1.z.string().nullable(),
    city: zod_1.z.string().nullable(),
    country: zod_1.z.string().nullable(),
});
/** Operator contact details for a rest area, as stored on `RestAreaEntity.contact`/`RestAreaOverrideEntity.contact`. */
exports.RestAreaContactSchema = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string(),
    phone: zod_1.z.string(),
    email: zod_1.z.string(),
    website: zod_1.z.string(),
});
/** Provider-specific feature flags, as stored on `RestAreaEntity.features`/`RestAreaOverrideEntity.features`. */
exports.RestAreaFeaturesSchema = zod_1.z.record(zod_1.z.string(), zod_1.z.union([zod_1.z.boolean(), zod_1.z.array(zod_1.z.string())]));
/**
 * Default hard cap on displayed parking prices, in euros, absent a per-company override.
 * Single source of truth for both `packages/core` (server-side enforcement) and
 * `apps/mobile` (pre-load display fallback) — see `getMaxParkingPriceEurForUser` in
 * `restArea.service.ts`.
 */
exports.DEFAULT_MAX_PARKING_PRICE_EUR = 25;
/**
 * Absolute ceiling on any resolved price cap, regardless of company override — also the
 * limit booking checkout enforces. See `getMaxParkingPriceEurForUser` in `restArea.service.ts`
 * and the `CK_companies_max_parking_price_eur` check constraint on `CompanyEntity`.
 */
exports.ABSOLUTE_MAX_PARKING_PRICE_EUR = 100;
// The harmonized amenities every provider maps to (the MainFeatures keys in core).
// Supply-side: what a stop physically has. Shared by mobile's filter and ai-backend's voice replies.
// The literal-tuple annotation preserves the exact literal element types — the derived
// `EssentialAmenity` union below and ai-backend's `z.enum(ESSENTIAL_AMENITIES)` both depend
// on them, so a `readonly string[]` annotation would widen both.
exports.ESSENTIAL_AMENITIES = [
    'hasToilet',
    'hasShower',
    'hasGasStation',
    'hasRestaurant',
    'hasShop',
    'hasWifi',
    'hasSecurity',
];
exports.SECURITY_AMENITIES = [
    'hasLighting',
    'hasCameraMonitoring',
    'hasCCTV',
    'hasGate',
    'hasFencing',
    'hasGuardPersonal24',
    'hasRestrictedWalkAccess',
    'hasReceptionParking',
    'hasCertificate',
    'hasTAPA',
    'hasESPORG',
    'hasUmbruecken',
];
// Same value set the site-feedback flow calls `SiteFeedbackType`. That type was
// previously hand-duplicated in apps/routing-functions/src/service/feedback.service.ts
// and apps/mobile/src/api/feedback.ts — both now alias `SiteCategory` from here
// instead of redefining the literals, so this schema is the single source of truth.
exports.SiteCategorySchema = zod_1.z.enum([
    'Rastanlage',
    'Autohof',
    'Sonstiges',
]);
exports.SiteFeaturesSchema = zod_1.z.object({
    hasToilet: zod_1.z.boolean().default(false),
    hasShower: zod_1.z.boolean().default(false),
    hasGasStation: zod_1.z.boolean().default(false),
    hasRestaurant: zod_1.z.boolean().default(false),
    hasShop: zod_1.z.boolean().default(false),
    hasWifi: zod_1.z.boolean().default(false),
    hasSecurity: zod_1.z.boolean().default(false),
    hasSecuredParking: zod_1.z.boolean().default(false),
    hasPowerConnection: zod_1.z.boolean().default(false),
    hasFastCharging: zod_1.z.boolean().default(false),
    hasTruckWash: zod_1.z.boolean().default(false),
    hasCameraMonitoring: zod_1.z.boolean().default(false),
    hasCashMachine: zod_1.z.boolean().default(false),
    hasPicnicFacilities: zod_1.z.boolean().default(false),
    hasPublicPhone: zod_1.z.boolean().default(false),
    hasTollTerminal: zod_1.z.boolean().default(false),
    hasDefibrillator: zod_1.z.boolean().default(false),
    hasHotel: zod_1.z.boolean().default(false),
    hasMotel: zod_1.z.boolean().default(false),
});
// Single source of truth for the amenity grid rendered by both the site-feedback
// screen and the "Standort melden" wizard — neither hand-types this list anymore.
exports.SITE_FEATURE_KEYS = exports.SiteFeaturesSchema.keyof().options;
exports.HourlyScoreEntrySchema = zod_1.z.object({
    score: zod_1.z.number(),
    isEmpty: zod_1.z.number(),
    isAlmostFull: zod_1.z.number(),
    isFull: zod_1.z.number(),
});
exports.HourlyScoresSchema = zod_1.z.object({
    '00': exports.HourlyScoreEntrySchema,
    '01': exports.HourlyScoreEntrySchema,
    '02': exports.HourlyScoreEntrySchema,
    '03': exports.HourlyScoreEntrySchema,
    '04': exports.HourlyScoreEntrySchema,
    '05': exports.HourlyScoreEntrySchema,
    '06': exports.HourlyScoreEntrySchema,
    '07': exports.HourlyScoreEntrySchema,
    '08': exports.HourlyScoreEntrySchema,
    '09': exports.HourlyScoreEntrySchema,
    '10': exports.HourlyScoreEntrySchema,
    '11': exports.HourlyScoreEntrySchema,
    '12': exports.HourlyScoreEntrySchema,
    '13': exports.HourlyScoreEntrySchema,
    '14': exports.HourlyScoreEntrySchema,
    '15': exports.HourlyScoreEntrySchema,
    '16': exports.HourlyScoreEntrySchema,
    '17': exports.HourlyScoreEntrySchema,
    '18': exports.HourlyScoreEntrySchema,
    '19': exports.HourlyScoreEntrySchema,
    '20': exports.HourlyScoreEntrySchema,
    '21': exports.HourlyScoreEntrySchema,
    '22': exports.HourlyScoreEntrySchema,
    '23': exports.HourlyScoreEntrySchema,
});
const ALMOST_FULL_FORECAST_SCORE = 70;
const FULL_FORECAST_SCORE = 90;
function classifyForecastScore(score) {
    if (score >= FULL_FORECAST_SCORE)
        return SiteAvailabilityStatus.Full;
    if (score >= ALMOST_FULL_FORECAST_SCORE)
        return SiteAvailabilityStatus.AlmostFull;
    return SiteAvailabilityStatus.SpacesAvailable;
}
// Deliberately a literal array, not `HourlyScoresSchema.keyof().options` — JS enumerates
// integer-index-like object keys ('10'-'23') in ascending numeric order before any other string
// key, so deriving this from the schema's key order silently produces
// ['10','11',...,'23','00','01',...,'09'] instead of '00'-'23' in sequence.
exports.HOUR_KEYS = [
    '00',
    '01',
    '02',
    '03',
    '04',
    '05',
    '06',
    '07',
    '08',
    '09',
    '10',
    '11',
    '12',
    '13',
    '14',
    '15',
    '16',
    '17',
    '18',
    '19',
    '20',
    '21',
    '22',
    '23',
];
function parseHourlyScores(value) {
    const result = exports.HourlyScoresSchema.safeParse(value);
    if (result.success) {
        return { valid: true, hourlyScores: result.data };
    }
    return {
        valid: false,
        problems: result.error.issues.map((issue) => `${issue.path.join('.')}: ${issue.message}`),
    };
}
exports.AvailabilityForecastSchema = zod_1.z.object({
    siteId: zod_1.z.string(),
    dayName: zod_1.z.string(),
    hourlyScores: exports.HourlyScoresSchema,
});
//# sourceMappingURL=rest-area.js.map